export type TenantPlan = "Go" | "Growth" | "Scale" | "Enterprise";
export type TenantStatus = "active" | "exempt" | "suspended" | "trial";
export type TenantVertical =
  | "Facility Mgmt"
  | "Generic"
  | "HVAC"
  | "Pest Control"
  | "Telco";

export type Tenant = {
  readonly billing: "Exempt" | "Manual / Invoice" | "Stripe";
  readonly code: string;
  readonly country: string;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly createdBy?: string;
  readonly id: string;
  readonly lastLogin: string;
  readonly name: string;
  readonly owner: string;
  readonly ownerName: string;
  readonly plan: TenantPlan;
  readonly planVersion: number;
  readonly product: "Enterprise" | "SaaS";
  readonly status: TenantStatus;
  readonly stripeId?: string;
  readonly suspendedReason?: string;
  readonly trialDays?: number;
  readonly users: number;
  readonly vertical: TenantVertical;
};

export const tenantPlans: readonly TenantPlan[] = [
  "Go",
  "Growth",
  "Scale",
  "Enterprise",
] as const;

export const tenantStatuses: readonly TenantStatus[] = [
  "active",
  "trial",
  "suspended",
  "exempt",
] as const;

export const tenantVerticals: readonly TenantVertical[] = [
  "HVAC",
  "Pest Control",
  "Facility Mgmt",
  "Telco",
  "Generic",
] as const;

export const tenants: readonly Tenant[] = [
  {
    billing: "Stripe",
    code: "ac-pro",
    country: "Costa Rica",
    countryCode: "CR",
    createdAt: "Mar 12, 2026",
    createdBy: "Ethan Reyes",
    id: "ac-pro",
    lastLogin: "2d ago",
    name: "AC Pro Services",
    owner: "owner@acpro.com",
    ownerName: "Carlos Mendez",
    plan: "Growth",
    planVersion: 3,
    product: "SaaS",
    status: "active",
    stripeId: "cus_8a3f2d91e",
    users: 12,
    vertical: "HVAC",
  },
  {
    billing: "Exempt",
    code: "claro-cr",
    country: "Costa Rica",
    countryCode: "CR",
    createdAt: "Jan 8, 2026",
    createdBy: "Olivia Sanchez",
    id: "claro-cr",
    lastLogin: "1h ago",
    name: "Claro Costa Rica",
    owner: "admin@claro.cr",
    ownerName: "Maria Solano",
    plan: "Enterprise",
    planVersion: 1,
    product: "Enterprise",
    status: "exempt",
    users: 124,
    vertical: "Telco",
  },
  {
    billing: "Stripe",
    code: "termistop",
    country: "Guatemala",
    countryCode: "GT",
    createdAt: "Apr 22, 2026",
    createdBy: "Ethan Reyes",
    id: "termistop",
    lastLogin: "4h ago",
    name: "TermiStop Inc",
    owner: "owner@termistop.com",
    ownerName: "Carlos Lopez",
    plan: "Go",
    planVersion: 2,
    product: "SaaS",
    status: "trial",
    stripeId: "cus_NkR8h2FxA4L9",
    trialDays: 9,
    users: 3,
    vertical: "Pest Control",
  },
  {
    billing: "Stripe",
    code: "facility-first",
    country: "Costa Rica",
    countryCode: "CR",
    createdAt: "Feb 22, 2026",
    id: "facility-first",
    lastLogin: "3d ago",
    name: "FacilityFirst Solutions",
    owner: "admin@facility-first.com",
    ownerName: "Jose Vargas",
    plan: "Scale",
    planVersion: 2,
    product: "SaaS",
    status: "active",
    users: 28,
    vertical: "Facility Mgmt",
  },
  {
    billing: "Stripe",
    code: "coolbreeze",
    country: "Guatemala",
    countryCode: "GT",
    createdAt: "Mar 28, 2026",
    id: "coolbreeze",
    lastLogin: "1d ago",
    name: "CoolBreeze HVAC",
    owner: "carlos.r@coolbreeze.com",
    ownerName: "Carlos Ramirez",
    plan: "Growth",
    planVersion: 3,
    product: "SaaS",
    status: "active",
    users: 8,
    vertical: "HVAC",
  },
  {
    billing: "Exempt",
    code: "tigo-hn",
    country: "Honduras",
    countryCode: "HN",
    createdAt: "Feb 4, 2026",
    id: "tigo-hn",
    lastLogin: "30m ago",
    name: "Tigo Honduras",
    owner: "admin@tigo.hn",
    ownerName: "Roberto Castillo",
    plan: "Enterprise",
    planVersion: 1,
    product: "Enterprise",
    status: "exempt",
    users: 89,
    vertical: "Telco",
  },
  {
    billing: "Stripe",
    code: "acmepest",
    country: "Panama",
    countryCode: "PA",
    createdAt: "Mar 18, 2026",
    id: "acmepest",
    lastLogin: "5d ago",
    name: "AcmePest Co",
    owner: "owner@acmepest.com",
    ownerName: "Ana Mendoza",
    plan: "Growth",
    planVersion: 3,
    product: "SaaS",
    status: "active",
    users: 6,
    vertical: "Pest Control",
  },
  {
    billing: "Stripe",
    code: "hvac-masters",
    country: "Panama",
    countryCode: "PA",
    createdAt: "Feb 15, 2026",
    id: "hvac-masters",
    lastLogin: "22d ago",
    name: "HVAC Masters Panama",
    owner: "owner@hvac-masters.com",
    ownerName: "Pedro Cortez",
    plan: "Go",
    planVersion: 2,
    product: "SaaS",
    status: "suspended",
    stripeId: "cus_overdue",
    suspendedReason: "22 days overdue - payment failure",
    users: 4,
    vertical: "HVAC",
  },
  {
    billing: "Stripe",
    code: "pestpro-cr",
    country: "Costa Rica",
    countryCode: "CR",
    createdAt: "Mar 5, 2026",
    id: "pestpro-cr",
    lastLogin: "6h ago",
    name: "PestPro Costa Rica",
    owner: "admin@pestpro.cr",
    ownerName: "Luis Quesada",
    plan: "Growth",
    planVersion: 3,
    product: "SaaS",
    status: "active",
    users: 7,
    vertical: "Pest Control",
  },
  {
    billing: "Stripe",
    code: "aircool-gt",
    country: "Guatemala",
    countryCode: "GT",
    createdAt: "Apr 1, 2026",
    id: "aircool-gt",
    lastLogin: "2d ago",
    name: "AirCool Guatemala",
    owner: "owner@aircool.gt",
    ownerName: "Sofia Hernandez",
    plan: "Go",
    planVersion: 2,
    product: "SaaS",
    status: "active",
    users: 2,
    vertical: "HVAC",
  },
  {
    billing: "Exempt",
    code: "movistar-pa",
    country: "Panama",
    countryCode: "PA",
    createdAt: "Feb 12, 2026",
    id: "movistar-pa",
    lastLogin: "1d ago",
    name: "Movistar Panama",
    owner: "admin@movistar.pa",
    ownerName: "Ricardo Nunez",
    plan: "Enterprise",
    planVersion: 1,
    product: "Enterprise",
    status: "active",
    users: 54,
    vertical: "Telco",
  },
  {
    billing: "Stripe",
    code: "hvac-tropical",
    country: "Costa Rica",
    countryCode: "CR",
    createdAt: "Mar 22, 2026",
    id: "hvac-tropical",
    lastLogin: "12h ago",
    name: "HVAC Tropical",
    owner: "owner@hvactropical.com",
    ownerName: "Paula Rojas",
    plan: "Growth",
    planVersion: 3,
    product: "SaaS",
    status: "active",
    users: 9,
    vertical: "HVAC",
  },
  {
    billing: "Stripe",
    code: "climacontrol",
    country: "Costa Rica",
    countryCode: "CR",
    createdAt: "Mar 30, 2026",
    id: "climacontrol",
    lastLogin: "5h ago",
    name: "ClimaControl SA",
    owner: "admin@climacontrol.com",
    ownerName: "Daniel Soto",
    plan: "Growth",
    planVersion: 3,
    product: "SaaS",
    status: "active",
    users: 11,
    vertical: "HVAC",
  },
  {
    billing: "Stripe",
    code: "pestguard-pa",
    country: "Panama",
    countryCode: "PA",
    createdAt: "Apr 18, 2026",
    id: "pestguard-pa",
    lastLogin: "1d ago",
    name: "PestGuard Panama",
    owner: "info@pestguard.pa",
    ownerName: "Carmen Acosta",
    plan: "Go",
    planVersion: 2,
    product: "SaaS",
    status: "trial",
    trialDays: 5,
    users: 2,
    vertical: "Pest Control",
  },
  {
    billing: "Stripe",
    code: "fmpro-cr",
    country: "Costa Rica",
    countryCode: "CR",
    createdAt: "Feb 28, 2026",
    id: "fmpro-cr",
    lastLogin: "2d ago",
    name: "FacilityPro CR",
    owner: "admin@fmpro.cr",
    ownerName: "Eduardo Mendez",
    plan: "Scale",
    planVersion: 2,
    product: "SaaS",
    status: "active",
    users: 18,
    vertical: "Facility Mgmt",
  },
  {
    billing: "Stripe",
    code: "aireperfecto",
    country: "Honduras",
    countryCode: "HN",
    createdAt: "Mar 25, 2026",
    id: "aireperfecto",
    lastLogin: "3d ago",
    name: "AirePerfecto",
    owner: "owner@aireperfecto.com",
    ownerName: "Patricia Velazquez",
    plan: "Growth",
    planVersion: 3,
    product: "SaaS",
    status: "active",
    users: 5,
    vertical: "HVAC",
  },
  {
    billing: "Stripe",
    code: "termiplaga",
    country: "Honduras",
    countryCode: "HN",
    createdAt: "Apr 5, 2026",
    id: "termiplaga",
    lastLogin: "1d ago",
    name: "TermiPlaga",
    owner: "admin@termiplaga.com",
    ownerName: "Marco Rivera",
    plan: "Go",
    planVersion: 2,
    product: "SaaS",
    status: "active",
    users: 3,
    vertical: "Pest Control",
  },
  {
    billing: "Stripe",
    code: "edificios-pa",
    country: "Panama",
    countryCode: "PA",
    createdAt: "Feb 18, 2026",
    id: "edificios-pa",
    lastLogin: "4h ago",
    name: "Edificios Panama",
    owner: "admin@edificios.pa",
    ownerName: "Jorge Restrepo",
    plan: "Scale",
    planVersion: 2,
    product: "SaaS",
    status: "active",
    users: 22,
    vertical: "Facility Mgmt",
  },
  {
    billing: "Stripe",
    code: "hvac-elite",
    country: "Guatemala",
    countryCode: "GT",
    createdAt: "Apr 16, 2026",
    id: "hvac-elite",
    lastLogin: "8h ago",
    name: "HVAC Elite",
    owner: "owner@hvacelite.gt",
    ownerName: "Fernanda Cruz",
    plan: "Growth",
    planVersion: 3,
    product: "SaaS",
    status: "trial",
    trialDays: 11,
    users: 4,
    vertical: "HVAC",
  },
  {
    billing: "Stripe",
    code: "cleanfac",
    country: "Costa Rica",
    countryCode: "CR",
    createdAt: "Mar 14, 2026",
    id: "cleanfac",
    lastLogin: "2d ago",
    name: "CleanFac Solutions",
    owner: "admin@cleanfac.com",
    ownerName: "Andres Solis",
    plan: "Growth",
    planVersion: 3,
    product: "SaaS",
    status: "active",
    users: 7,
    vertical: "Facility Mgmt",
  },
  {
    billing: "Stripe",
    code: "fumihogar",
    country: "Costa Rica",
    countryCode: "CR",
    createdAt: "Apr 8, 2026",
    id: "fumihogar",
    lastLogin: "5d ago",
    name: "FumiHogar",
    owner: "owner@fumihogar.cr",
    ownerName: "Lucia Vargas",
    plan: "Go",
    planVersion: 2,
    product: "SaaS",
    status: "active",
    users: 2,
    vertical: "Pest Control",
  },
  {
    billing: "Stripe",
    code: "serviciogeneral",
    country: "Guatemala",
    countryCode: "GT",
    createdAt: "Mar 19, 2026",
    id: "serviciogeneral",
    lastLogin: "1d ago",
    name: "Servicio General SA",
    owner: "admin@serviciogeneral.gt",
    ownerName: "Miguel Torres",
    plan: "Growth",
    planVersion: 3,
    product: "SaaS",
    status: "active",
    users: 6,
    vertical: "Generic",
  },
  {
    billing: "Manual / Invoice",
    code: "mantenimientocr",
    country: "Costa Rica",
    countryCode: "CR",
    createdAt: "Feb 22, 2026",
    id: "mantenimientocr",
    lastLogin: "15d ago",
    name: "MantenimientoCR",
    owner: "admin@mantenimientocr.com",
    ownerName: "Beatriz Mora",
    plan: "Go",
    planVersion: 2,
    product: "SaaS",
    status: "suspended",
    suspendedReason: "Customer requested account hold",
    users: 3,
    vertical: "Generic",
  },
];
